import styled, { css } from 'styled-components';
import {
    primitiveColors,
    device,
    fadeIn,
    radius,
    size,
    slideIn
} from '@/styles/template';
import {ArticleH2} from '@/components/atoms/UI/Typography';

export const ModalWrap = styled.div`
    color: ${props => props.theme.semantic.text.primary} !important;
    position: fixed;
    overflow: hidden;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 1000;
`;

interface ContainerProps {
    $colorful?: boolean;
    $mode?: 'successPrompt' | 'errorPrompt';
}

export const Container = styled.div<ContainerProps>`
    position: absolute;
    overflow: hidden auto;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background: ${({ theme }) => theme.semantic.surface.background};
    /* Докрутка к элементу (scroll-margin-top) останавливается под липкой шапкой. */
    --scroll-offset-top: 7rem;


    ${({ $mode }) => ($mode === 'successPrompt' || $mode === 'errorPrompt') && css`
        color: ${primitiveColors.gray[50]};
        width: fit-content;
        height: fit-content;
        padding: 6rem!important;
        border-radius: ${(p) => p.theme.primitives?.radius?.lg ?? radius.large};

        /* prompt'ы рендерятся с unclosed → без StickyBar. ContentWrapper
           рассчитан на sticky-крестик (padding-top: 0, увеличенный bottom),
           поэтому без крестика снизу набегает лишний отступ: 6rem Container +
           bottom ContentWrapper'a — низ оказывается вдвое больше верха. Внутри
           prompt'а отступ симметрично держит сам Container, а вложенный
           ContentWrapper гасим. Здесь у Container единственный потомок — он. */
        & > div {
            padding: 0 !important;
        }

        /* edge case: короткое сообщение без заголовка (нет ModalTitle/<h2>) —
           6rem вокруг одной строки смотрятся избыточно, поджимаем до 4rem. */
        &:not(:has(h2)) {
            padding: 4rem!important;
        }
    `}

    ${({ $mode, theme }) => ($mode === 'successPrompt') && css`
        background: ${theme.brand?.secondary ?? primitiveColors.secondary[600]};
    `}
    
    ${({ $mode, theme }) => ($mode === 'errorPrompt') && css`
        background: ${theme.brand?.error ?? primitiveColors.error[500]};
    `}
    
    max-width: ${size.tablet};
    margin: auto;
    /* padding живёт на ContentWrapper, чтобы sticky-крестик
       (StickyBar, первый ребёнок) был в правом верхнем углу,
       а не сдвинут padding-ом вниз. */

    /* Контейнер получает инициальный фокус (tabindex=-1 в Modal.tsx) — гасим
       кольцо: это диалог-обёртка, а не интерактивный контрол. */
    &:focus { outline: none; }

    /* Пружинный slide-in (squash-and-stretch overshoot) — единый для мобилы и
       десктопа. На full-screen мобиле он безопасен: ModalWrap (overflow:hidden)
       клипает выход scale-пружины за вьюпорт, а 100dvh при открытии модалки не
       пересчитывается (адресная строка не двигается без скролла). Прежний
       «прыжок/подвисание» давала не пружина, а focus-induced scroll — он дёргал
       scroll-контейнер во время transform; устранён preventScroll в Modal.tsx. */
    animation: ${slideIn} .6s cubic-bezier(0.280, 0.840, 0.420, 1);

  @media ${device.tablet} {
      border-radius: ${(p) => p.theme.primitives?.radius?.lg ?? radius.large};
      max-height: 90vh;
      height: fit-content;
      //width: fit-content;
  }

    box-shadow: ${({ theme }) => theme.semantic.elevation.modal};
`

/** Внутренний контейнер с padding'ом — отделён от Container ради того,
 *  чтобы StickyBar (sticky-шапка 7rem, выше) лёг в (0,0) Container'a
 *  и крестик встал ровно в правый верхний угол модалки. padding-top = 0,
 *  место сверху уже даёт sticky-шапка. */
export const ContentWrapper = styled.div`
    padding: 0 2rem 3rem;

    @media ${device.tablet} {
        padding: 0 6rem 6rem;
    }
`;

export const Overlay = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    background-color: ${({ theme }) => theme.semantic.surface.overlay};
    animation: ${fadeIn} .7s cubic-bezier(0.280, 0.840, 0.420, 1);
`;

export const ModalTitle = styled(ArticleH2)`
    color: ${({ theme }) => theme.semantic.text.primary};
`

/**
 * Sticky-«шапка» модалки: зоны start/center заполняет содержимое через
 * ModalStickyBarSlot, в end — крестик.
 *   height: 7rem          — занимает место в потоке = высота крестика;
 *                           заголовок ниже без overlap, padding-top на
 *                           ContentWrapper не нужен.
 *   background            — фон идентичен Container (surface.background); при
 *                           скролле перекрывает контент, чтобы текст не
 *                           «просвечивал» через крестик.
 *   gradient 70% → 100%   — мягкий fade-out низа шапки: контент «растворяется»
 *                           в фон, видно куда уплывают элементы при скролле.
 *   position: sticky/top  — приклеена к верху Container'a (overflow: hidden auto),
 *                           крестик и зоны всегда видны.
 *   z-index: 2            — положительный, не 0: при z-index:0 sticky-шапка и
 *                           позиционированный контент (напр. карточки
 *                           PricingItemWrap, position: relative / z-index: auto)
 *                           оказываются в одной группе стекинга и рисуются по
 *                           DOM-порядку — шапка идёт раньше, поэтому контент
 *                           ложился поверх крестика. Положительный z-index
 *                           поднимает шапку над auto-контентом. Значение 2
 *                           синхронизировано с vanilla .fb-universal__sticky-top
 *                           (resources/css/bx/main.css).
 *   pointer-events: none  — шапка во всю ширину и при z-index:2 перехватывала
 *                           бы клики по всей верхней полосе, перекрывая контент
 *                           под прозрачным низом градиента. Кликабельны только
 *                           зоны (pointer-events: auto).
 *   direction: ltr        — «Назад» слева, крестик справа и в ar/ur.
 * Эта же схема в vanilla (.fb-universal__sticky-top + .fb-universal__close,
 * bx/main.css) — высота, градиент и z-index в паритете; зон start/center там нет.
 */
export const StickyBar = styled.div`
    position: sticky;
    top: 0;
    height: 7rem;
    z-index: 2;
    display: flex;
    align-items: center;
    direction: ltr;
    pointer-events: none;
    background:  linear-gradient(
        to bottom,
        ${({ theme }) => theme.semantic.surface.background} 0%,
        ${({ theme }) => theme.semantic.surface.background} 70%,
        transparent 100%
    );
`;

export const StickyBarStart = styled.div`
    flex: none;
    display: flex;
    align-items: center;
    pointer-events: auto;
`;

/** Пока левый угол пуст — отступ как у ContentWrapper, чтобы содержимое встало над контентом. */
export const StickyBarCenter = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    pointer-events: auto;

    ${StickyBarStart}:empty + & {
        padding-inline-start: 2rem;

        @media ${device.tablet} {
            padding-inline-start: 6rem;
        }
    }
`;

export const StickyBarEnd = styled.div`
    flex: none;
    display: flex;
    pointer-events: auto;
`;

/** Пока копия в шапке, оригинал прячем — иначе он просвечивает через прозрачный низ градиента. */
export const DockAnchor = styled.div<{ $hidden: boolean }>`
    visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export const DockedCopy = styled.div`
    width: 100%;
    min-width: 0;
    animation: ${fadeIn} .15s ease-out;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

export const PopupInnerVerticalStretch = styled.div`
  	/* 100vh на iOS Safari = «большой» вьюпорт (как будто панели браузера
  	   скрыты), поэтому контейнер выше реально видимой области: низ с кнопками
  	   уезжает за фолд, а space-between раздувает пустоту между контентом и
  	   кнопками. 100dvh учитывает динамические панели iPhone. vh оставлен
  	   фолбэком для браузеров без поддержки dvh (iOS < 15.4).

  	   Вычитаемое = высота обёрток внутри скроллируемого Container'a, иначе
  	   контент переливает вьюпорт и появляется скролл:
  	     7rem — StickyBar (sticky-шапка, занимает место в потоке)
  	   + 3rem — ContentWrapper padding-bottom (мобайл)
  	   = 10rem. При изменении этих величин — пересчитать здесь. */
  	min-height: calc(100vh - 10rem);
  	min-height: calc(100dvh - 10rem);
    display: flex;
    gap: 3rem;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: space-between;
	@media ${device.tablet} {
		min-height: auto;
	}
`